// Reconciliation (SCR-013): does the proxy's metering match Bedrock's own meter, and did every
// request it served reach the ledger? Bedrock drift and metering completeness for one customer
// (API-024, API-025), over a range of months.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import type { ScreenProps } from "../../router/route";
import { RouterContext } from "../../router/router";
import { CustomerPicker, useCustomerName } from "../../ui/customer-directory";
import { Field, LabelText } from "@nexus/ui";
import { MonthRangePicker } from "@nexus/ui";
import { currentMonth, type MonthRange } from "@nexus/ui";
import { Card, Chip, CopyId, EmptyState, FilterBar, PageHeader } from "@nexus/ui";
import { useBreadcrumb } from "../../ui/shell";
import { DataTable, type Column } from "@nexus/ui";
import {
  MAX_RANGE_MONTHS,
  bedrockQuery,
  completenessPercent,
  completenessQuery,
  completenessRows,
  countOrDash,
  defaultRange,
  driftRows,
  installationLabel,
  rangeFilters,
  signedPercent,
  targetOrgPath,
  type BedrockReconciliation,
  type Completeness,
  type CompletenessRowView,
  type DriftRowView,
} from "./model";

interface FetchState<T> {
  loading: boolean;
  data: T | null;
  error: ApiError | null;
}

const LEDE = "Checks that what the proxy metered matches Bedrock's own meter, and that every request it served reached the ledger.";

/**
 * NAV-009's own node (`/reconciliation`): reconciliation is always read for one customer, so this
 * is the customer picker and what the screen is for.
 */
export function ReconciliationIndex(_props: ScreenProps<"/reconciliation">) {
  const router = useContext(RouterContext);
  const go = (orgId: string) => {
    const path = targetOrgPath(orgId);
    if (path && router) router.navigate(path);
  };

  return (
    <div class="page reconciliation-screen">
      <PageHeader title="Reconciliation" lede={LEDE} />
      <FilterBar>
        <Field id="reconciliation-customer" label="Customer" help="common.customer" compact class="field-medium">
          <CustomerPicker id="reconciliation-customer" value="" onChange={go} />
        </Field>
      </FilterBar>
      <EmptyState
        title="Pick a customer to reconcile"
        lede="Reconciliation reads one customer at a time, over up to three months."
        steps={[
          { title: "Bedrock drift", text: "Each installation's requests compared with Bedrock's CloudWatch meter, per day and model." },
          { title: "Metering completeness", text: "Requests the proxy served against events the ledger ingested, spilled and dropped." },
        ]}
      />
    </div>
  );
}

export function Reconciliation({ params }: ScreenProps<"/reconciliation/:orgId">) {
  const client = useApi();
  const router = useContext(RouterContext);
  const { orgId } = params;
  const name = useCustomerName(orgId);
  useBreadcrumb([{ label: name }]);

  const [range, setRange] = useState<MonthRange>(() => defaultRange(new Date()));
  const [drift, setDrift] = useState<FetchState<BedrockReconciliation>>({ loading: true, data: null, error: null });
  const [completeness, setCompleteness] = useState<FetchState<Completeness>>({ loading: true, data: null, error: null });
  const [installations, setInstallations] = useState<ReadonlyMap<string, string>>(new Map());
  const [reloadToken, setReloadToken] = useState(0);
  const filters = rangeFilters(range);
  const filtersKey = `${filters.from}/${filters.to}`;

  useEffect(() => {
    let cancelled = false;
    setDrift((current) => ({ ...current, loading: true, error: null }));
    client
      .get("/v1/orgs/{orgId}/reconciliation/bedrock", { path: { orgId }, query: bedrockQuery(filters) })
      .then((data) => {
        if (!cancelled) setDrift({ loading: false, data, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError) setDrift({ loading: false, data: null, error });
        else throw error;
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId, filtersKey, reloadToken]);

  useEffect(() => {
    let cancelled = false;
    setCompleteness((current) => ({ ...current, loading: true, error: null }));
    client
      .get("/v1/orgs/{orgId}/reconciliation/completeness", { path: { orgId }, query: completenessQuery(filters) })
      .then((data) => {
        if (!cancelled) setCompleteness({ loading: false, data, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError) setCompleteness({ loading: false, data: null, error });
        else throw error;
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId, filtersKey, reloadToken]);

  // Installation names, where the principal may read them; otherwise the id's start stands in.
  useEffect(() => {
    let cancelled = false;
    client
      .get("/v1/orgs/{orgId}/installations", { path: { orgId } })
      .then((page) => {
        if (!cancelled) setInstallations(new Map(page.items.map((installation) => [installation.id, installation.name])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [client, orgId]);

  const reload = () => setReloadToken((token) => token + 1);
  const gotoOrg = (next: string) => {
    const path = targetOrgPath(next);
    if (path && router && next !== orgId) router.navigate(path);
  };

  const installationCell = (id: string) => {
    const label = installationLabel(installations, id);
    return (
      <span class="nowrap" title={id}>
        {label}
      </span>
    );
  };

  const driftColumns: Column<DriftRowView>[] = [
    { key: "installation", header: "Installation", cell: (r) => installationCell(r.item.installationId), compare: (a, b) => a.item.installationId.localeCompare(b.item.installationId) },
    { key: "day", header: "Day", cell: (r) => <span class="nowrap">{r.item.day}</span>, compare: (a, b) => a.item.day.localeCompare(b.item.day) },
    { key: "model", header: "Model", cell: (r) => <span class="mono">{r.item.modelId}</span>, compare: (a, b) => a.item.modelId.localeCompare(b.item.modelId) },
    { key: "ours", header: "Proxy requests", numeric: true, cell: (r) => r.item.ours.requests.toLocaleString("en-US") },
    { key: "bedrock", header: "Bedrock invocations", numeric: true, cell: (r) => (r.item.bedrock === null ? "—" : r.item.bedrock.invocations.toLocaleString("en-US")) },
    {
      key: "drift",
      header: "Drift",
      numeric: true,
      help: "finance.drift",
      cell: (r) => signedPercent(r.item.driftBp?.requests),
      compare: (a, b) => Math.abs(a.item.driftBp?.requests ?? 0) - Math.abs(b.item.driftBp?.requests ?? 0),
    },
    {
      key: "overThreshold",
      header: "Threshold",
      help: "finance.driftStatus",
      cell: (r) => (r.overThreshold ? <Chip tone="amber">Over threshold</Chip> : <Chip tone="green">Within threshold</Chip>),
      compare: (a, b) => Number(a.overThreshold) - Number(b.overThreshold),
    },
    { key: "findings", header: "Findings", help: "finance.findings", cell: (r) => (r.findingLabels.length === 0 ? "—" : r.findingLabels.join("; ")) },
  ];

  const completenessColumns: Column<CompletenessRowView>[] = [
    { key: "installation", header: "Installation", cell: (r) => installationCell(r.item.installationId) },
    { key: "served", header: "Served", numeric: true, cell: (r) => countOrDash(r.item.requestsServed) },
    { key: "ingested", header: "Ingested", numeric: true, cell: (r) => r.item.eventsIngested.toLocaleString("en-US") },
    { key: "spilled", header: "Spilled", numeric: true, help: "finance.spilled", cell: (r) => countOrDash(r.item.eventsSpilled) },
    { key: "dropped", header: "Dropped", numeric: true, help: "finance.dropped", cell: (r) => countOrDash(r.item.eventsDropped) },
    { key: "completeness", header: "Completeness", numeric: true, help: "finance.completenessPct", cell: (r) => completenessPercent(r.item.completenessBp) },
    {
      key: "status",
      header: "Status",
      help: "common.status",
      cell: (r) =>
        r.incomplete ? (
          <Chip tone="amber">{r.item.countersComplete ? "Incomplete" : "Counters missing"}</Chip>
        ) : (
          <Chip tone="green">Complete</Chip>
        ),
    },
  ];

  const threshold = drift.data ? signedPercent(drift.data.driftThresholdBp).replace("+", "") : null;
  const over = drift.data ? drift.data.items.filter((item) => item.overThreshold).length : 0;

  return (
    <div class="page reconciliation-screen">
      <PageHeader title={`Reconciliation · ${name}`} lede={LEDE} meta={<CopyId id={orgId} label="Customer ID" what="customer ID" />} />
      <FilterBar>
        <Field id="reconciliation-customer" label="Customer" help="common.customer" compact class="field-medium">
          <CustomerPicker id="reconciliation-customer" value={orgId} onChange={gotoOrg} />
        </Field>
        <div class="filter">
          <LabelText id="reconciliation-months-label" label="Months" help="common.months" compact />
          <MonthRangePicker
            id="reconciliation-months"
            labelledBy="reconciliation-months-label"
            value={range}
            onChange={setRange}
            maxSpan={MAX_RANGE_MONTHS}
            max={currentMonth(new Date())}
          />
        </div>
      </FilterBar>

      <div class="banner banner-info">
        <p>Bedrock's CloudWatch metrics cover the whole AWS account: they include any use of the account that did not go through the proxy.</p>
      </div>

      <Card title="Bedrock drift" help="finance.bedrockDrift">
        {drift.data && (
          <p class="card-note">
            Alarm threshold {threshold}. {over === 0 ? "No cell is over it." : `${over} ${over === 1 ? "cell is" : "cells are"} over it.`}
          </p>
        )}
        <DataTable
          caption="Bedrock drift"
          hideCaption
          columns={driftColumns}
          rows={drift.data ? driftRows(drift.data) : []}
          rowKey={(r) => `${r.item.installationId}@${r.item.day}@${r.item.modelId}`}
          loading={drift.loading}
          error={drift.error}
          onRetry={reload}
          emptyMessage="No Bedrock usage reported for these months."
        />
      </Card>

      <Card title="Metering completeness" help="finance.completeness">
        <DataTable
          caption="Metering completeness"
          hideCaption
          columns={completenessColumns}
          rows={completeness.data ? completenessRows(completeness.data) : []}
          rowKey={(r) => r.item.installationId}
          loading={completeness.loading}
          error={completeness.error}
          onRetry={reload}
          emptyMessage="No installation reported for these months."
        />
      </Card>
    </div>
  );
}
